import React, { useContext, useState, useCallback } from "react";
import { ColorModeContext, tokens } from "../../theme";
import { AppBar, Box, Toolbar, Popover, useTheme, Link } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "slices/auth";
import { Link as RouterLink } from "react-router-dom";
import MegaMenu from "./MegaMenu";
import { clearMessage } from "slices/message";
import NavBarPreferences from "./NavBarPreferences";
import { updatePreferenceByUser } from "slices/auth";
import { createSearchPrompt } from "slices/search";
import NavBarSearch from "./NavBarSearch";
import FlexBetween from "components/global/FlexBetween";
import NavbarSearchBar from "components/global/navbar/NavbarSearchBar";
import NavbarUserMenu from "components/global/navbar/NavbarUserMenu";
import NavbarActions from "components/global/navbar/NavbarActions";

const Navbar = ({
  copilotSize,
  setCopilotSize,
  isCopilotOpen,
  setIsCopilotOpen,
  isNavMenuOpen,
  setIsNavMenuOpen,
  menuArea,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);

  const { user: currentUser } = useSelector((state) => state.auth);
  const { isLoggedIn } = useSelector((state) => state.auth);

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const handleClick = () => setIsUserMenuOpen((v) => !v);
  const handleClose = () => setIsUserMenuOpen(false);

  const [anchorElSearch, setAnchorElSearch] = useState(null);
  const isOpenSearch = Boolean(anchorElSearch);
  const handleOpenSearch = () => {
    dispatch(clearMessage());
    setAnchorElSearch(searchRef.current);
  };
  const handleCloseSearch = () => setAnchorElSearch(null);

  const [searchPromptText, setSearchPromptText] = useState("");
  const [anchorElPreferences, setAnchorElPreferences] = useState(null);
  const [areaHeight] = useState("60vh");
  const [areaWidth] = useState("50vw");

  const configRef = React.useRef(null);
  const chatRef = React.useRef(null);
  const searchRef = React.useRef(null);

  const [areaHeightSearch] = useState("60vh");
  const [areaHeightSearchList] = useState("50vh");
  const [areaWidthSearch] = useState("70vw");

  const handleOpenPreferences = () => {
    dispatch(clearMessage());
    setAnchorElPreferences(configRef.current);
  };

  const handelClosePreferences = () => setAnchorElPreferences(null);

  const handleToggleDarkMode = () => {
    colorMode.toggleColorMode();
    dispatch(clearMessage());

    const preference = {
      preference_value: theme.palette.mode === "light" ? "dark" : "light",
      name: "useDarkMode",
      username: currentUser.user.username,
    };
    dispatch(updatePreferenceByUser({ preference }));
  };

  const openPreferences = Boolean(anchorElPreferences);

  const handelToggleCopilot = () => {
    setIsCopilotOpen(isCopilotOpen ? false : true);
  };

  const imgSrc = `../../assets/img/Logo_long.png`;

  const handleSearchChange = (event) => {
    setSearchPromptText(event.target.value);
  };

  const handleSearch = () => {
    if (searchPromptText !== "") {
      const searchPrompt = { prompt: searchPromptText };
      dispatch(createSearchPrompt({ searchPrompt }));
      setSearchPromptText("");
      handleOpenSearch();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <AppBar
      sx={{
        position: "fixed",
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 1px 0 rgba(255,255,255,0.02), 0 6px 18px rgba(0,0,0,0.35)"
            : "0 1px 2px rgba(15, 23, 42, 0.03), 0 4px 14px rgba(15, 23, 42, 0.04)",
        backdropFilter: "saturate(140%) blur(8px)",
        WebkitBackdropFilter: "saturate(140%) blur(8px)",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: "60px !important", gap: 2 }}>
        <FlexBetween gap="1rem">
          <Link component={RouterLink} to="/" display="flex">
            <img alt="Logo" height="35px" src={imgSrc} />
          </Link>

          <MegaMenu />
        </FlexBetween>

        <NavbarSearchBar
          searchRef={searchRef}
          searchPromptText={searchPromptText}
          onSearchChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onSearch={handleSearch}
          onOpenSearch={handleOpenSearch}
          theme={theme}
        />

        <FlexBetween gap=".5rem">
          <NavbarActions
            isLoggedIn={isLoggedIn}
            isCopilotOpen={isCopilotOpen}
            onToggleCopilot={handelToggleCopilot}
            onToggleDarkMode={handleToggleDarkMode}
            onOpenPreferences={handleOpenPreferences}
            configRef={configRef}
            theme={theme}
          />

          <NavbarUserMenu
            chatRef={chatRef}
            handleClick={handleClick}
            isOpen={isUserMenuOpen}
            handleClose={handleClose}
            isLoggedIn={isLoggedIn}
            currentUser={currentUser}
            logOut={logOut}
            theme={theme}
          />
        </FlexBetween>
      </Toolbar>

      <Popover
        open={openPreferences}
        anchorEl={anchorElPreferences}
        onClose={handelClosePreferences}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box height={areaHeight} width={areaWidth}>
          <NavBarPreferences
            copilotOpenSetter={setIsCopilotOpen}
            copilotSizeSetter={setCopilotSize}
            copilotSize={copilotSize}
          />
        </Box>
      </Popover>

      <Popover
        open={isOpenSearch}
        anchorEl={anchorElSearch}
        onClose={handleCloseSearch}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box height={areaHeightSearch} width={areaWidthSearch}>
          <NavBarSearch maxHeightList={areaHeightSearchList} closer={handleCloseSearch} />
        </Box>
      </Popover>
    </AppBar>
  );
};

export default Navbar;
